/**
 * I corpi in ingresso dell'ufficio: l'organico, il calendario, il piano del
 * giorno.
 *
 * Qui la scrittura e la lettura **non hanno la stessa forma**, ed è il motivo
 * per cui i nomi si distinguono: `zOperatrice` e `zTurni` in `risposte.ts` sono
 * ciò che l'ufficio legge — con l'`id`, il `login` e `attiva` — mentre
 * `zScriviOperatrice` e `zScriviTurni` sono ciò che manda. Finché la scrittura
 * viveva dentro il controller i due omonimi non si vedevano, e il backoffice si
 * ricavava la forma di scrittura con un `Pick<>` sulla lettura: la stessa cosa
 * dichiarata tre volte.
 *
 * Il giorno di pianificazione **non sta qui**: la sua validazione vera è
 * `eGiorno` di `@errebi/domain` (`2026-02-30` rispetta la forma e non esiste), e
 * `shared` non può dipendere dal dominio perché finisce nel bundle del browser.
 * Resta accanto alla rotta, in `pianificazione.controller.ts`.
 */
import { z } from 'zod';
import { zReparto } from './responses.js';
/**
 * Un'operatrice come la si scrive.
 *
 * L'`id` **non c'è**, ed è deliberato: Zod scarta le chiavi che non conosce,
 * quindi un id nel corpo si ferma qui invece di arrivare all'archivio. È la
 * chiave dei giorni già pianificati, e sceglierla vorrebbe dire poterla
 * riusare per due persone. Nemmeno `attiva`: chi entra e chi esce dalla forza
 * si cambia da un'altra rotta, perché non è modificare un'anagrafica.
 *
 * `nome` si sfronda prima di misurarlo: tre spazi non sono un nome, e una
 * colonna intitolata a niente non si riconosce sulla plancia.
 */
export const zScriviOperatrice = z.object({
    nome: z.string().trim().min(1, 'il nome non può essere vuoto'),
    reparto: zReparto,
    // Positive perché una colonna a capacità zero non è pianificabile: la barra
    // sarebbe sempre «oltre le sue ore» anche senza un ordine assegnato.
    ore: z.number().positive('le ore devono essere più di zero'),
    utente: z.string().nullable(),
});
/** Chi entra e chi esce dalla forza. */
export const zCambiaForza = z.object({ attiva: z.boolean() });
/**
 * Il calendario di lavoro come lo si scrive.
 *
 * Qui c'è solo la **forma**: cinque caratteri `HH:MM`, sette righe al massimo,
 * la pausa presente o assente per intero. Che gli orari abbiano senso fra loro
 * lo dice `motivoRifiutoTurni`, che sta nel dominio perché la stessa risposta
 * la deve dare il modulo prima di mandare.
 *
 * Un giorno chiuso è una riga che **non c'è**: nessun campo `chiuso`, che
 * sarebbe un secondo modo di dire la stessa cosa.
 */
const zOrario = z.string().regex(/^\d{2}:\d{2}$/, 'l’orario si scrive 08:00');
export const zScriviTurni = z
    .array(z.object({
    giorno: z.number().int().min(0).max(6),
    nome: z.string().trim().min(1),
    da: zOrario,
    a: zOrario,
    pausa: z.tuple([zOrario, zOrario]).nullable(),
}))
    .max(7, 'la settimana ha sette giorni');
export const zAssegnaGiorno = z.object({
    ordine: z.string().min(1),
    /** `null` toglie l'assegnazione; l'assenza del campo è un errore. */
    operatrice: z.string().nullable(),
});
//# sourceMappingURL=planning.js.map